import * as React from "react"
import * as PopoverPrimitive from "@radix-ui/react-popover"

import { cn } from "@/lib/utils"

/**
 * Hybrid Tooltip API backed by Radix Popover so that (i)-style info icons
 * work properly on both touch and pointer devices.
 *
 * Behavior:
 *   • Hover-capable devices (mouse, fine pointer):
 *       – Hover trigger or content → opens
 *       – Mouse leaves both → closes after a short grace period
 *       – Focus (keyboard) → opens; blur → closes
 *       – Click trigger → toggles (acts as a "pin"/dismiss)
 *   • Touch-primary devices (coarse pointer, no hover):
 *       – Tap trigger → opens
 *       – Tap outside the popover → closes
 *       – Tap trigger again → closes
 *       – Tapping another tip closes the first one (Radix collision logic)
 *
 * Positioning is collision-aware: Radix flips side automatically and clamps
 * horizontally within the viewport (with extra padding) so the popover never
 * overflows the screen edges on mobile.
 *
 * IMPORTANT: This is a drop-in replacement for the Shadcn Tooltip API. All
 * existing consumers (`Tooltip`, `TooltipTrigger`, `TooltipContent`,
 * `TooltipProvider`) continue to work without modification.
 */
const TooltipContext = React.createContext(null)

const TooltipProvider = ({ children }) => <>{children}</>

const useHoverCapable = () => {
  const [hoverCapable, setHoverCapable] = React.useState(() => {
    if (typeof window === "undefined") return true
    return window.matchMedia("(hover: hover) and (pointer: fine)").matches
  })
  React.useEffect(() => {
    if (typeof window === "undefined") return undefined
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)")
    const onChange = (e) => setHoverCapable(e.matches)
    if (mq.addEventListener) mq.addEventListener("change", onChange)
    else mq.addListener(onChange)
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange)
      else mq.removeListener(onChange)
    }
  }, [])
  return hoverCapable
}

const Tooltip = ({
  children,
  open: controlledOpen,
  onOpenChange,
  defaultOpen,
  // Accepted for backwards-compat with the previous Radix Tooltip API; ignored
  // because Popover has its own grace-period behavior.
  // eslint-disable-next-line no-unused-vars
  delayDuration,
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen ?? false)
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : uncontrolledOpen
  const setOpen = React.useCallback(
    (value) => {
      if (!isControlled) setUncontrolledOpen(value)
      onOpenChange?.(value)
    },
    [isControlled, onOpenChange]
  )
  const hoverCapable = useHoverCapable()
  // Tracks whether the pointer is currently over the trigger or content.
  // While hovering, we suppress Radix Popover's automatic "click to close"
  // behavior so that clicking an already-hovered (i) icon does NOT
  // accidentally dismiss the popover — natural mouse-leave still closes it.
  const hoveringRef = React.useRef(false)
  // Wrapped onOpenChange handler given to PopoverPrimitive.Root. Radix calls
  // this for both intentional dismissals (click trigger when open, outside
  // click, Escape key) and our own programmatic transitions. We only want to
  // veto the click-while-hovering case.
  const handlePopoverOpenChange = React.useCallback(
    (next) => {
      if (next === false && hoverCapable && hoveringRef.current) return
      setOpen(next)
    },
    [hoverCapable, setOpen]
  )
  // Shared close timer between trigger and content so hovering from one to the
  // other doesn't accidentally dismiss the popover.
  const closeTimerRef = React.useRef(null)
  const cancelClose = React.useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }, [])
  const armClose = React.useCallback(() => {
    cancelClose()
    closeTimerRef.current = setTimeout(() => {
      // Only close if pointer has truly left both trigger and content.
      if (!hoveringRef.current) setOpen(false)
    }, 140)
  }, [cancelClose, setOpen])

  const ctx = React.useMemo(
    () => ({ open, setOpen, hoverCapable, cancelClose, armClose, hoveringRef }),
    [open, setOpen, hoverCapable, cancelClose, armClose]
  )
  return (
    <TooltipContext.Provider value={ctx}>
      <PopoverPrimitive.Root open={open} onOpenChange={handlePopoverOpenChange}>
        {children}
      </PopoverPrimitive.Root>
    </TooltipContext.Provider>
  )
}

const TooltipTrigger = React.forwardRef(
  ({ asChild = true, children, onMouseEnter, onMouseLeave, onFocus, onBlur, ...props }, ref) => {
    const ctx = React.useContext(TooltipContext) || {}
    const { setOpen, hoverCapable, cancelClose, armClose, hoveringRef } = ctx

    const handlers = hoverCapable
      ? {
          onMouseEnter: (e) => {
            if (hoveringRef) hoveringRef.current = true
            cancelClose?.()
            setOpen?.(true)
            onMouseEnter?.(e)
          },
          onMouseLeave: (e) => {
            if (hoveringRef) hoveringRef.current = false
            armClose?.()
            onMouseLeave?.(e)
          },
          onFocus: (e) => {
            cancelClose?.()
            setOpen?.(true)
            onFocus?.(e)
          },
          onBlur: (e) => {
            armClose?.()
            onBlur?.(e)
          },
        }
      : {}

    return (
      <PopoverPrimitive.Trigger asChild={asChild} ref={ref} {...handlers} {...props}>
        {children}
      </PopoverPrimitive.Trigger>
    )
  }
)
TooltipTrigger.displayName = "TooltipTrigger"

const TooltipContent = React.forwardRef(
  (
    {
      className,
      side = "top",
      align = "center",
      sideOffset = 6,
      collisionPadding = 12,
      children,
      onMouseEnter,
      onMouseLeave,
      ...props
    },
    ref
  ) => {
    const ctx = React.useContext(TooltipContext) || {}
    const { hoverCapable, cancelClose, armClose, hoveringRef } = ctx
    const hoverHandlers = hoverCapable
      ? {
          onMouseEnter: (e) => {
            if (hoveringRef) hoveringRef.current = true
            cancelClose?.()
            onMouseEnter?.(e)
          },
          onMouseLeave: (e) => {
            if (hoveringRef) hoveringRef.current = false
            armClose?.()
            onMouseLeave?.(e)
          },
        }
      : {}
    return (
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          ref={ref}
          side={side}
          align={align}
          sideOffset={sideOffset}
          avoidCollisions
          collisionPadding={collisionPadding}
          // Prevent auto-focusing the content on open so the page doesn't
          // scroll when the popover appears near the bottom of the screen.
          onOpenAutoFocus={(e) => e.preventDefault()}
          {...hoverHandlers}
          className={cn(
            // Premium dark surface with subtle border + soft shadow
            "z-50 w-auto max-w-[min(280px,calc(100vw-24px))] rounded-lg border border-border/70 bg-popover/95 backdrop-blur-xl px-3 py-2.5 text-[12px] leading-relaxed text-popover-foreground shadow-[var(--app-shadow-elev-2)] outline-none",
            // Open/close animations consistent with the original Tooltip shim
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2",
            "origin-[--radix-popover-content-transform-origin]",
            className
          )}
          {...props}
        >
          {children}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    )
  }
)
TooltipContent.displayName = "TooltipContent"

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
